import { Component, Input, OnInit, output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map, tap, withLatestFrom } from 'rxjs/operators';

import { SingleProductTemplateConfigurations } from '../../configs/single-product-template.configs';
import { SingleProductTemplateModel } from '../../models/single-product-template-model';
import { StudioTemplatesService } from '../../services/studio-templates.service';

import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { getCatalogStyles } from '@pages/category/store/category-page.selectors';
import { ProductViewConfig, CategoryConfiguration, GridProductStyle } from '@shared/product/models';

@Component({
    selector: 'studio-single-product-template',
    templateUrl: './studio-single-product-template.component.html',
})

/**
 * Componente para mostrar la plantilla single product de Zara Studio
 */
export class StudioSingleProductTemplateComponent implements OnInit {
    @Input() public categoryId: string;

    @Input() public products: number[] = [];

    @Input() public categoryConfiguration: CategoryConfiguration;

    @Input() public templateName: string;

    @Input() public place: string;

    @Input() public elementIndex: number;

    @Input() public elementDefaultImageTypes: Map<number, string>;

    @Input() public isMediaCarouselActivated: boolean;

    @Input() public productStyle: GridProductStyle;

    public readonly clickAddToCart = output<ProductPublicModels.PublicProduct>();

    public templateConfiguration$: Observable<ProductViewConfig>;

    public templateSettings: SingleProductTemplateModel;

    public hasCmsStyles: boolean;

    public templateCmsStyles: Record<string, string>;

    public skeletonClass: string;

    public templatePlace: string;

    constructor(
        private readonly store: Store,
        private readonly singleProductTemplateConfigurations: SingleProductTemplateConfigurations,
    ) { }

    public ngOnInit(): void {
        this.initTemplateConfiguration();
    }

    public initTemplateConfiguration(): void {
        this.templateSettings = this.singleProductTemplateConfigurations.getConfig()[this.templateName];

        if (this.templateSettings) {
            this.hasCmsStyles = !!this.templateSettings.cmsStyles;

            this.templateConfiguration$ = this.store.select(getResolution).pipe(
                first(),
                withLatestFrom(
                    this.store.select(getCatalogStyles),
                    this.store.select(getCmsConfigValue(this.templateSettings.cmsStyles)),
                ),
                tap(([resolution]) => { this.calculateSkLoad(resolution); }),
                map(([resolution, catalogStyles, cmsStyles]: [string, Record<string, string>, Record<string, string>]) => {
                    const templateConfiguration = StudioTemplatesService.mapTemplateConfiguration(
                        resolution,
                        catalogStyles,
                        cmsStyles,
                        this.templateSettings,
                        this.place
                    );

                    this.templateCmsStyles = cmsStyles;
                    templateConfiguration.showMediaCarousel = this.isMediaCarouselActivated;

                    return templateConfiguration;
                })
            );
        } else {
            console.error(`Settings for template ${this.templateName} not found on SingleProductTemplate`);
        }
    }

    public addToCartSheet({ product }) {
        this.clickAddToCart.emit(product);
    }

    private calculateSkLoad(resolution: string): void {
        if (!this.templateSettings.skLoad) return;

        this.skeletonClass = resolution === this.templateSettings.skLoad.useMobileConfig
            ? `sk-load${this.templateSettings.skLoad.mobile}`
            : `sk-load${this.templateSettings.skLoad.desktop}`;
    }
}
